import * as PIXI from "pixi.js";
import { Keyboard, Key } from "./keyboard";
import {
	GAME_WIDTH,
	GAME_HEIGHT,
	STARTING_PLAYER_X,
	STARTING_PLAYER_Y,
	DEBUG,
	PLAYER_WIDTH,
	GUN_DISTANCE_FROM_PLAYER,
	BULLET_SPEED,
	PLAYER_CUTOFF,
} from "./constants";

export class Player {
	public sprite: PIXI.Sprite;
	public gunSprite: PIXI.Sprite;
	public collisionBox: PIXI.Rectangle;
	public graphics: PIXI.Graphics;
	public vy: number;
	public grav: number;
	public vx: number;
	public mouse: PIXI.Point;
	public fallingDeath: boolean;

	constructor(mouse: PIXI.Point) {
		this.sprite = new PIXI.Sprite(
			PIXI.Loader.shared.resources["img/player/p_right.png"].texture
		);
		this.sprite.anchor.set(0.5);
		this.sprite.x = STARTING_PLAYER_X;
		this.sprite.y = STARTING_PLAYER_Y;

		this.gunSprite = new PIXI.Sprite(
			PIXI.Loader.shared.resources["img/player/p_gun.png"].texture
		);
		this.gunSprite.anchor.set(0.5);
		this.gunSprite.x = STARTING_PLAYER_X;
		this.gunSprite.y = STARTING_PLAYER_Y;

		this.collisionBox = new PIXI.Rectangle(
			this.sprite.x - this.sprite.width / 2,
			this.sprite.y - this.sprite.height / 2,
			this.sprite.width,
			this.sprite.height
		);

		this.graphics = new PIXI.Graphics();
		this.vx = 0;
		this.vy = 0;
		this.grav = 0.5;
		this.fallingDeath = false;
		this.mouse = mouse;
	}
	updateDeath(delta: number, shouldFall: boolean): void {
		if (shouldFall) {
			if (!this.fallingDeath) {
				this.vy = 0;
			}
			this.vy += this.grav * delta;
			this.sprite.y += this.vy * delta;
			this.gunSprite.y += this.vy * delta;
			this.fallingDeath = true;
		} else {
			this.vy -= this.grav * delta;
			if (this.sprite.y < PLAYER_CUTOFF) {
				this.sprite.y = PLAYER_CUTOFF;
			} else {
				this.sprite.y += this.vy * delta;
			}
			const target = this.pointDistanceFrom(
				this.sprite.x,
				this.sprite.y,
				this.mouse.x,
				this.mouse.y,
				GUN_DISTANCE_FROM_PLAYER
			);

			this.gunSprite.x = target[0];
			this.gunSprite.y = target[1];

			const facing = Math.sign(this.mouse.x - this.sprite.x);
			if (
				this.gunSprite.y > this.sprite.y ||
				this.gunSprite.x > this.sprite.x + GUN_DISTANCE_FROM_PLAYER
			) {
				this.gunSprite.y = this.sprite.y;
				this.gunSprite.x = this.sprite.x + facing * GUN_DISTANCE_FROM_PLAYER;
			} else {
				this.gunSprite.rotation = -target[2] * facing;
			}
			this.sprite.scale.set(facing, 1);
			this.gunSprite.scale.set(facing, 1);
		}
	}

	update(delta: number): void {
		let speedWanted = 0; // The wanted horizontal speed for this step\

		if (Keyboard.check(Key.a)) {
			speedWanted -= 6;
		}
		if (Keyboard.check(Key.d)) {
			speedWanted += 6;
		}

		this.vx += (speedWanted - this.vx) * 0.2;
		this.sprite.x += this.vx;

		if (this.sprite.x < 0) this.sprite.x = GAME_WIDTH;
		if (this.sprite.x > GAME_WIDTH) this.sprite.x = 0;
		this.vy += this.grav * delta;
		this.sprite.y += this.vy * delta;

		if (this.sprite.y < PLAYER_CUTOFF) {
			this.sprite.y = PLAYER_CUTOFF;
		}

		this.collisionBox.x = this.sprite.x - this.sprite.width / 2;
		this.collisionBox.y = this.sprite.y - this.sprite.height / 2;

		const target = this.pointDistanceFrom(
			this.sprite.x,
			this.sprite.y,
			this.mouse.x,
			this.mouse.y,
			GUN_DISTANCE_FROM_PLAYER
		);

		this.gunSprite.x = target[0];
		this.gunSprite.y = target[1];

		const facing = Math.sign(this.mouse.x - this.sprite.x);
		if (
			this.gunSprite.y > this.sprite.y ||
			this.gunSprite.x > this.sprite.x + GUN_DISTANCE_FROM_PLAYER
		) {
			this.gunSprite.y = this.sprite.y;
			this.gunSprite.x = this.sprite.x + facing * GUN_DISTANCE_FROM_PLAYER;
		} else {
			this.gunSprite.rotation = -target[2] * facing;
		}
		this.sprite.scale.set(facing, 1);
		this.gunSprite.scale.set(facing, 1);

		if (DEBUG) {
			this.graphics.clear();
			this.graphics.lineStyle(2, 0x0000ff); // (thickness, color)
			this.graphics.drawRect(
				this.collisionBox.x,
				this.collisionBox.y,
				this.collisionBox.width,
				this.collisionBox.height
			);
			this.graphics.endFill();
		}
	}
	pointDistanceFrom(
		xSource: number,
		ySource: number,
		xTarget: number,
		yTarget: number,
		distance: number
	) {
		const distanceToTarget = Math.sqrt(
			Math.pow(xTarget - xSource, 2) + Math.pow(yTarget - ySource, 2)
		);
		const xFinal =
			xSource + (distance * (xTarget - xSource)) / distanceToTarget;
		const yFinal =
			ySource + (distance * (yTarget - ySource)) / distanceToTarget;

		let angle = Math.acos((xFinal - xSource) / distance);
		if (angle > Math.PI / 2) {
			angle = Math.PI - angle;
		}

		return [xFinal, yFinal, angle];
	}
}
